import { Suspense, useState } from 'react';

import { Constants } from '@constants';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { tracking } from '@iwoca/frontend-tracking-library';
import {
    IconAngleDown,
    IconAngleRight,
    IconAngleUp,
    IconCheckDefault,
    IconTimesDefault
} from '@iwoca/icons';
import { Button, Label, Modal, Text } from '@iwoca/orion';
import { maintainStateKey } from '@iwRouter/LocationHelper';
import { Skeleton } from '@ui';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';

import { NextRepayment } from './NextRepayment';
import {
    TUseRCFWithdrawFunds,
    useRCFWithdrawFunds
} from './useRCFWithdrawFunds';
import { ViewNewPaymentPlan } from './ViewNewPaymentPlan';
import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import { HelpModalContent } from '../../../navigation/helpModal/HelpModalContent';
import { ChooseAmountInputWithSlider } from '../../../ui/ChooseAmountInputWithSlider/ChooseAmountInputWithSlider';
import { isSelectedAmountValidForAccessPeriod } from '../../../ui/ChooseAmountInputWithSlider/isSelectedAmountValid';

export const RCFWithdrawFunds = ({ accountId }: { accountId: string }) => {
    return (
        <ErrorBoundary
            fallback={(error) => <FallbackErrorModal error={error} />}
        >
            <Suspense
                fallback={
                    <div
                        className="space-y-m rounded-s border border-secondary-90 p-l"
                        data-testid="rcf-withdraw-funds-loading"
                    >
                        <Skeleton className="h-[10px]" />
                        <Skeleton className="h-[10px]" />
                    </div>
                }
            >
                <WithdrawFunds accountId={accountId} />
            </Suspense>
        </ErrorBoundary>
    );
};

const WithdrawFunds = ({ accountId }: { accountId: string }) => {
    const [selectAmountPanelOpen, setSelectAmountPanelOpen] = useState(false);
    const { outstandingBalance, formalOfferAccessPeriod, isOverdue } =
        useRCFWithdrawFunds(accountId);

    const withdrawalsPaused = formalOfferAccessPeriod.withdrawals_paused;

    const isDisabled = withdrawalsPaused || isOverdue;

    if (
        formalOfferAccessPeriod.available_cash < Constants.MIN_DRAW_DOWN_AMOUNT
    ) {
        return null;
    }

    return (
        <>
            <div
                className="rounded-s border border-secondary-90"
                data-testid="rcf-withdraw-funds"
            >
                <div
                    className={classNames(
                        'flex flex-col gap-m rounded-s p-l min-sm:flex-row min-sm:items-center',
                        selectAmountPanelOpen
                            ? 'border-b border-secondary-90'
                            : ''
                    )}
                >
                    <Button
                        disabled={isDisabled}
                        onClick={() => {
                            if (!selectAmountPanelOpen) {
                                tracking.ampli.rcfWithdrawFundsClicked();
                            }
                            setSelectAmountPanelOpen((prevState) => !prevState);
                        }}
                    >
                        <FormattedMessage
                            id="_.outstanding_view.rcf_withdraw_money"
                            defaultMessage="Withdraw money"
                        />
                        {selectAmountPanelOpen ? (
                            <IconAngleUp />
                        ) : (
                            <IconAngleDown />
                        )}
                    </Button>
                    <WithdrawFundsMessage
                        isOverdue={isOverdue}
                        withdrawalsPaused={withdrawalsPaused}
                    />
                </div>
                {selectAmountPanelOpen && (
                    <SelectAmountPanel
                        accessPeriod={formalOfferAccessPeriod}
                        outstandingBalance={outstandingBalance}
                        closePanel={() => setSelectAmountPanelOpen(false)}
                    />
                )}
            </div>
        </>
    );
};

const SelectAmountPanel = ({
    accessPeriod,
    outstandingBalance,
    closePanel
}: {
    accessPeriod: TUseRCFWithdrawFunds['formalOfferAccessPeriod'];
    outstandingBalance: number;
    closePanel: () => void;
}) => {
    const availableCash = accessPeriod.available_cash;
    const history = useHistory();

    const [selectedAmount, setSelectedAmount] = useState<number>(availableCash);

    const newOutstandingBalance = outstandingBalance + selectedAmount;

    const goToFundingRequirements = () => {
        history.push(
            maintainStateKey({
                pathname: '/funding-requirements/',
                search: `?offerId=${accessPeriod.offer_id}&amount=${selectedAmount}`
            })
        );
    };

    return (
        <div className="flex flex-col p-l" data-testid="withdrawal-panel">
            <button
                className="ml-auto rounded-xs p-s hover:bg-secondary-95"
                onClick={closePanel}
                aria-label="Close withdraw money panel"
            >
                <IconTimesDefault className="size-[16px] shrink-0 fill-structure-0" />
            </button>
            <div className="flex flex-col gap-xl min-sm:flex-row">
                <div className="flex-1">
                    <ChooseAmountInputWithSlider
                        label={
                            <Label htmlFor="choose-amount">
                                <FormattedMessage
                                    id="_.outstanding_view.rcf_select_amount_label"
                                    defaultMessage="What amount would you like to withdraw today?"
                                />
                            </Label>
                        }
                        maxAmount={availableCash}
                        selectedAmount={selectedAmount}
                        onChange={(value) => setSelectedAmount(value)}
                    />
                </div>
                <div className="flex-1 space-y-xl [&_p]:my-0">
                    <div className="flex justify-between">
                        <Text className="font-reg">
                            <FormattedMessage
                                id="_.outstanding_view.rcf_new_outstanding_loan_amount"
                                defaultMessage="New outstanding loan amount"
                            />
                        </Text>
                        <Text className="text-l font-bold">
                            <FormattedCurrency value={newOutstandingBalance} />
                        </Text>
                    </div>
                    <div className="flex justify-between">
                        <Text className="font-reg">
                            <FormattedMessage
                                id="_.outstanding_view.rcf_withdrawal_fees"
                                defaultMessage="Withdrawal fees"
                            />
                        </Text>

                        <Text className="text-l font-bold">
                            <FormattedCurrency value={0} />
                        </Text>
                    </div>
                    <Suspense
                        fallback={
                            <div className="space-y-xl">
                                <Skeleton className="h-[25px]" />
                                <Skeleton className="h-[25px]" />
                            </div>
                        }
                    >
                        <NextRepayment
                            accessPeriod={accessPeriod}
                            selectedAmount={selectedAmount}
                        />
                        <div className="my-xl">
                            <ViewNewPaymentPlan
                                drawdownAmount={selectedAmount}
                                accessPeriod={accessPeriod}
                            />
                        </div>
                    </Suspense>
                    <Button
                        variant="success"
                        className="w-full"
                        onClick={goToFundingRequirements}
                        disabled={
                            !isSelectedAmountValidForAccessPeriod(
                                selectedAmount,
                                accessPeriod
                            )
                        }
                    >
                        <span>
                            <FormattedMessage
                                id="_.outstanding_view.rcf_proceed_with_amount"
                                defaultMessage="Proceed with {amount}"
                                values={{
                                    amount: (
                                        <FormattedCurrency
                                            value={
                                                selectedAmount ?? availableCash
                                            }
                                            decimalPlaces={0}
                                        />
                                    )
                                }}
                            />
                        </span>
                        <IconAngleRight />
                    </Button>
                </div>
            </div>
        </div>
    );
};
const WithdrawFundsMessage = ({
    withdrawalsPaused,
    isOverdue
}: {
    withdrawalsPaused: boolean;
    isOverdue: boolean;
}) => {
    const [helpModalOpen, setHelpModalOpen] = useState(false);

    if (isOverdue)
        return (
            <Text className="my-0 text-s font-bold">
                <FormattedMessage
                    id="_.outstanding_view.rcf_no_withdrawals_possible_overdue"
                    defaultMessage="Currently, no further withdrawals are possible. <span>Please settle the overdue amount to re-enable withdrawals.</span>"
                    values={{
                        span: (chunks) => (
                            <span className="font-reg">{chunks}</span>
                        )
                    }}
                />
            </Text>
        );

    if (withdrawalsPaused)
        return (
            <>
                <Modal
                    isOpen={helpModalOpen}
                    onClose={() => setHelpModalOpen(false)}
                    closeIcon
                >
                    <HelpModalContent />
                </Modal>
                <div className="space-y-2xs">
                    <Text className="my-0 text-s font-bold">
                        <FormattedMessage
                            id="_.outstanding_view.rcf_no_withdrawals_possible"
                            defaultMessage="Currently, no further withdrawals are possible."
                        />
                    </Text>
                    <Text className="text-s font-reg">
                        <FormattedMessage
                            id="_.outstanding_view.rcf_review_message"
                            defaultMessage="We conduct regular reviews to adjust your financing to your current business situation. Please {contactTeamLink} to discuss your situation together."
                            values={{
                                contactTeamLink: (
                                    <button
                                        className="font-inherit cursor-pointer border-none bg-transparent p-0 link"
                                        onClick={() => setHelpModalOpen(true)}
                                    >
                                        <FormattedMessage
                                            id="_.outstanding_view.rcf_contact_team"
                                            defaultMessage="contact our team"
                                        />
                                    </button>
                                )
                            }}
                        />
                    </Text>
                </div>
            </>
        );

    return (
        <div className="space-y-xs [&_p]:m-0">
            <div className="flex items-center gap-s">
                <IconCheckDefault className="shrink-0 fill-success-drk" />
                <Text className="text-s font-reg" color="text-secondary">
                    <FormattedMessage
                        id="_.outstanding_view.rcf_payout_within_a_few_hours"
                        defaultMessage="Payout within a few hours"
                    />
                </Text>
            </div>
            <div className="flex items-center gap-s">
                <IconCheckDefault className="shrink-0 fill-success-drk" />
                <Text className="text-s font-reg" color="text-secondary">
                    <FormattedMessage
                        id="_.outstanding_view.rcf_without_reapplying"
                        defaultMessage="Without reapplying"
                    />
                </Text>
            </div>
        </div>
    );
};
